import React from 'react';

// The built-in user guide (#/help). Static: it needs no API call, so it opens even when the shop cannot load.
const TOC = [
  ['help-about', 'About the shop'],
  ['help-tour', 'The shop, part by part'],
  ['help-order', 'Order a box, step by step'],
  ['help-track', 'Follow or cancel an order'],
  ['help-prices', 'Prices and discounts'],
  ['help-tasks', 'Common tasks'],
  ['help-trouble', 'Troubleshooting'],
  ['help-faq', 'FAQ'],
];

const jump = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export function HelpPage({ onBack }) {
  return (
    <section className="help-page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <button type="button" className="link-btn" onClick={onBack}>Shop</button> <span aria-hidden="true">/</span> Help
      </nav>
      <p className="kicker">Help</p>
      <h1>How to use La Maison des Macarons</h1>
      <p className="lead">Everything the shop can do, how to order a box, and what to do when something goes wrong.</p>

      <nav className="help-toc" aria-label="Help contents">
        <ol>
          {TOC.map(([id, title]) => (
            <li key={id}><a href={`#${id}`} onClick={jump(id)}>{title}</a></li>
          ))}
        </ol>
      </nav>

      <h2 id="help-about">1. About the shop</h2>
      <p>
        La Maison des Macarons is a <strong>demo shop</strong> on devkuban.com. You pick macarons, put them in a box,
        and place an order for pickup at the counter. <strong>Nothing is paid online</strong>: you pay when you collect.
        Because it is a demo, use a made-up name and an address like <code>you@example.com</code>.
      </p>
      <p>No account is needed. Your box is kept in this browser until you order or empty it.</p>

      <h2 id="help-tour">2. The shop, part by part</h2>
      <h3>Top of the page</h3>
      <ul>
        <li><strong>Offers ticker</strong>: the running strip with today's offers (box discounts, free pickup, pay when you collect).</li>
        <li><strong>Logo</strong>: back to the start of the shop.</li>
        <li><strong>Shop</strong>: scrolls to the flavours. <strong>Bestsellers</strong>: shows only the bestsellers. <strong>Visit us</strong>: opening hours and pickup at the bottom. <strong>Help</strong>: this guide.</li>
        <li><strong>Box</strong> (bag icon): opens your box; the number is how many macarons are in it.</li>
      </ul>
      <h3>Home page</h3>
      <ul>
        <li><strong>Build your box</strong> and <strong>Our bestsellers</strong> jump to the flavours.</li>
        <li><strong>The favourites</strong>: the three bestsellers, with quick add buttons.</li>
        <li><strong>Pick your flavours</strong>: the full list with search and filters (below).</li>
        <li>Further down: gift boxes, the kitchen story, parties, and the footer with <strong>opening hours</strong> and <strong>pickup</strong> rules.</li>
      </ul>
      <h3>Finding macarons</h3>
      <table>
        <thead><tr><th>Control</th><th>What it does</th></tr></thead>
        <tbody>
          <tr><td>Search box</td><td>Finds flavours by name or ingredient, e.g. <code>pistachio</code> or <code>lemon</code>. The list updates as you type.</td></tr>
          <tr><td>Collections</td><td><em>all, bestseller, classic, fruity, floral, nutty, tea, seasonal</em>. Click one to show only that collection; click it again to show all.</td></tr>
          <tr><td>Flavour</td><td>Shows one flavour family only; <em>All flavours</em> shows everything.</td></tr>
          <tr><td>Sort</td><td><em>Featured, Name, Price: low to high, Price: high to low</em>.</td></tr>
          <tr><td>In stock only</td><td>Hides macarons that are sold out today.</td></tr>
        </tbody>
      </table>
      <h3>Macaron cards and the macaron page</h3>
      <ul>
        <li>Each card shows the photo, name, price and labels (<em>Bestseller</em>, <em>Seasonal</em>, <em>Sold out</em>).</li>
        <li><strong>Add</strong> puts one in your box; <strong>−</strong> and <strong>+</strong> change how many.</li>
        <li>Click the photo or name to open the <strong>macaron page</strong>: description, quick quantities, <strong>Ingredients</strong>, <strong>Allergens</strong>, <strong>Keeping</strong> advice (best within 3 days, chilled) and <em>You may also like</em>. <strong>Back to the shop</strong> returns to the list.</li>
        <li><em>Sold out today</em> means it is back tomorrow morning. <em>All in your box</em> means you already have all the stock.</li>
      </ul>
      <h3>Your box (the drawer)</h3>
      <ul>
        <li>Every macaron with its quantity and price; <strong>−</strong> / <strong>+</strong> and <strong>Remove</strong> change it; <strong>Empty the box</strong> clears it.</li>
        <li>The <strong>discount bar</strong> shows how many more you need for the next discount.</li>
        <li><strong>Checkout</strong> moves to your details; <strong>Back to the box</strong> returns.</li>
        <li>On a phone, a <strong>View box</strong> bar at the bottom shows the count and total once you have scrolled past the top.</li>
      </ul>

      <h2 id="help-order">3. Order a box, step by step</h2>
      <ol>
        <li>Pick macarons: <strong>Add</strong> on the cards, or set a quantity on a macaron page.</li>
        <li>Open the <strong>Box</strong> and check the list and total. Add more to reach 12 (10% off) or 24 (15% off).</li>
        <li>Press <strong>Checkout</strong>.</li>
        <li>Fill in <strong>Name</strong> and <strong>Email</strong>. Optionally choose a <strong>Pickup date</strong> (tomorrow up to 60 days ahead) and a <strong>Note for the kitchen</strong> (gift message, allergies).</li>
        <li>Press <strong>Place order · &lt;total&gt;</strong>. You see <em>Placing your order</em>, then your order page with its number.</li>
        <li><strong>Bookmark the order page</strong>: its address is how you find the order again.</li>
        <li>On pickup day, come from 10:00 with the order number and pay at the counter.</li>
      </ol>
      <p>You can order up to <strong>96 macarons</strong> online, at most 48 of one flavour. For towers and larger parties, talk to us at the counter.</p>

      <h2 id="help-track">4. Follow or cancel an order</h2>
      <ul>
        <li>Open the order page from your bookmark. It shows the progress: <em>Received → Baking → Ready for pickup → Collected</em>, the items, the discount and the <strong>Total (pay at pickup)</strong>.</li>
        <li><strong>Cancel order</strong> appears only while the order is <em>Received</em>, before the kitchen starts baking. After you confirm, the status shows <em>Cancelled</em>.</li>
      </ul>

      <h2 id="help-prices">5. Prices and discounts</h2>
      <ul>
        <li>Each macaron has its own price, shown on the card.</li>
        <li><strong>10% off</strong> a box of 12 or more, <strong>15% off</strong> 24 or more. The discount is applied to the whole box automatically.</li>
        <li>Prices are confirmed by the shop when you order; the order page shows the final total.</li>
      </ul>

      <h2 id="help-tasks">6. Common tasks</h2>
      <h3>Build a mixed box of 12</h3>
      <ol>
        <li>Press <strong>Build your box</strong>.</li>
        <li>Add 12 different flavours (or any mix); watch the discount bar reach 10%.</li>
        <li>Open the box and press <strong>Checkout</strong>.</li>
      </ol>
      <h3>Order a gift with a message</h3>
      <ol>
        <li>Fill your box, then <strong>Checkout</strong>.</li>
        <li>Write the message in <strong>Note for the kitchen</strong>; it is added with the ribbon.</li>
        <li>Pick the pickup date of the party.</li>
      </ol>
      <h3>Avoid an allergen</h3>
      <ol>
        <li>Open each macaron's page and read <strong>Allergens</strong>. All shells contain almonds and egg.</li>
        <li>Add a note for the kitchen about the allergy.</li>
      </ol>
      <h3>Find only fruity flavours that are in stock</h3>
      <ol>
        <li>Click the <em>fruity</em> collection and tick <strong>In stock only</strong>.</li>
      </ol>

      <h2 id="help-trouble">7. Troubleshooting</h2>
      <table>
        <thead><tr><th>What you see</th><th>What to do</th></tr></thead>
        <tbody>
          <tr><td>"Oops! Something went wrong" with <strong>Try again</strong></td><td>The shop could not load its macarons. Press <strong>Try again</strong>, or reload the page in a minute.</td></tr>
          <tr><td>"Search failed: …"</td><td>Only the current search failed. Change the search or try again; your box is safe.</td></tr>
          <tr><td>"No macarons match"</td><td>Clear the search, choose <em>all</em> collections or untick <em>In stock only</em>.</td></tr>
          <tr><td>"Your last order was not confirmed" / <em>Unconfirmed order</em> in the box</td><td>The connection dropped while ordering, so we do not know if it went through. Open the box: <strong>Retry the order</strong> sends the same order again (it will not be placed twice), or <strong>Discard it</strong>.</td></tr>
          <tr><td><strong>Checkout</strong> or <strong>Place order</strong> is greyed out</td><td>Fill in name and a valid email, keep the box at 96 macarons or fewer, and wait for <em>Loading the shop's prices…</em> to finish.</td></tr>
          <tr><td>"We could not find that order."</td><td>The order address is wrong or incomplete. Use the full link from your bookmark.</td></tr>
          <tr><td>A macaron says <em>Sold out today</em></td><td>It is back tomorrow morning. Pick another flavour for now.</td></tr>
          <tr><td>Your box emptied itself</td><td>The box lives in this browser only. A private window, another browser or cleared site data starts a new box.</td></tr>
        </tbody>
      </table>

      <h2 id="help-faq">8. FAQ</h2>
      <dl>
        <dt>Do I pay online?</dt>
        <dd>No. You pay at the counter when you collect.</dd>
        <dt>Do I need an account?</dt>
        <dd>No. Your order page link is your receipt; keep it.</dd>
        <dt>When can I collect?</dt>
        <dd>From 10:00 on your pickup day. We are open Tuesday to Saturday 8:00–18:00 and Sunday 9:00–14:00, closed on Mondays.</dd>
        <dt>How long do macarons keep?</dt>
        <dd>Best within 3 days in the fridge. Take them out 20 minutes before eating.</dd>
        <dt>Can I change an order?</dt>
        <dd>Cancel it while that is still possible and place a new one.</dd>
        <dt>Is this a real bakery?</dt>
        <dd>No, it is a demo shop on devkuban.com. The photos were generated with AI.</dd>
      </dl>

      <div className="drawer-actions">
        <button type="button" className="btn btn-dark" onClick={onBack}>Back to the shop</button>
      </div>
    </section>
  );
}
